import * as React from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Link, Stack,
    Step, StepLabel, Stepper, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getAiWorkflowRun, getChannelArticleExecution } from '../../Api';
import { AiWorkflowRunDetailDto } from '../../models/dtos/AiDto';
import { CHANNEL_ARTICLE_WORKFLOW, ChannelArticleExecutionDto, ChannelArticleStatus, PublishingMatchDto } from '../../models/dtos/PublishingDto';
import AiWorkflowRunView from '../ai/AiWorkflowRunView';
import { formatAiDuration, formatAiUsd } from '../ai/AiShared';
import MatchResearchDialog from '../match-briefs/MatchResearchDialog';

/** The working stages shown as steps (NOT_STARTED is "received", COMPLETED / FAILED the end). */
const STEPS: ChannelArticleStatus[] = ['NOT_STARTED', 'ASSIGNED', 'COLLECTING_CONTEXT', 'WEB_RESEARCH', 'WRITING', 'VALIDATING', 'TRANSLATIONS', 'SAVING', 'COMPLETED'];

export const ARTICLE_STAGE_LABELS: Record<ChannelArticleStatus, string> = {
    NOT_STARTED: 'Received',
    ASSIGNED: 'Channel and match',
    COLLECTING_CONTEXT: 'Collecting context',
    WEB_RESEARCH: 'Web research',
    WRITING: 'Writing',
    VALIDATING: 'Validating',
    TRANSLATIONS: 'Translations',
    SAVING: 'Saving',
    COMPLETED: 'Completed',
    FAILED: 'Failed',
};

export function ArticleStageChip({ status }: { status: ChannelArticleStatus }) {
    const color = status === 'COMPLETED' ? 'success' : status === 'FAILED' ? 'error' : status === 'NOT_STARTED' ? 'default' : 'info';
    return <Chip size="small" color={color} variant={status === 'NOT_STARTED' ? 'outlined' : 'filled'} label={ARTICLE_STAGE_LABELS[status] ?? status} />;
}

export const publishingMatchTitle = (match: PublishingMatchDto | null | undefined) =>
    match ? (match.home && match.away ? `${match.home} – ${match.away}` : `Match ${match.id}`) : null;

const time = (iso?: string | null) => (iso ? new Date(iso).toLocaleTimeString() : '');

/**
 * One AI channel article invocation: its stages with their times, why it failed or had nothing to do, the post it
 * wrote, the research it used, and the AI hub view of its correlation id (cost per task and agent) with every call.
 */
export default function ChannelArticleExecutionDialog({ executionId, onClose, onOpenPost }: {
    executionId: number | null;
    onClose: () => void;
    /** Opens the post it wrote (Publishing: the post dialog); absent = a link to the channel page. */
    onOpenPost?: (postId: number, channelId: number) => void;
}) {
    const [execution, setExecution] = React.useState<ChannelArticleExecutionDto | null>(null);
    const [run, setRun] = React.useState<AiWorkflowRunDetailDto | null>(null);
    const [runLoading, setRunLoading] = React.useState(false);
    const [researchOpen, setResearchOpen] = React.useState(false);

    React.useEffect(() => {
        setExecution(null);
        setRun(null);
        if (executionId == null) return;
        let cancelled = false;
        getChannelArticleExecution(executionId).then(result => {
            if (cancelled) return;
            setExecution(result);
            if (result?.correlation_id) {
                setRunLoading(true);
                getAiWorkflowRun(CHANNEL_ARTICLE_WORKFLOW, result.correlation_id).then(r => {
                    if (!cancelled) { setRun(r); setRunLoading(false); }
                });
            }
        });
        return () => { cancelled = true; };
    }, [executionId]);

    const reached = (stage: ChannelArticleStatus) => execution?.stages?.find(s => s.stage === stage)?.at;
    const failedAt = execution?.status === 'FAILED' ? execution.failed_stage : null;
    const activeStep = execution
        ? execution.status === 'FAILED' ? STEPS.indexOf(failedAt ?? 'NOT_STARTED') : STEPS.indexOf(execution.status) + (execution.status === 'COMPLETED' ? 1 : 0)
        : 0;
    const title = execution ? publishingMatchTitle(execution.match) : null;

    return (
        <>
            <Dialog open={executionId != null} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
                <DialogTitle>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        <span>Article #{executionId}{execution?.channel_name ? ` · ${execution.channel_name}` : ''}{title ? ` · ${title}` : ''}</span>
                        {execution && <ArticleStageChip status={execution.status} />}
                        {execution && <Chip size="small" variant="outlined" label={execution.source.toLowerCase()} />}
                        {execution?.auto_published && <Chip size="small" color="success" variant="outlined" label="auto-published" />}
                    </Stack>
                    {execution && (
                        <Typography variant="body2" color="text.secondary">
                            Received {new Date(execution.created_at).toLocaleString()}
                            {execution.requested_by ? ` · by ${execution.requested_by}` : ''}
                            {execution.requested_channel_id ? '' : ' · channel picked automatically'}
                            {execution.requested_match_id ? ` · asked for match ${execution.requested_match_id}` : ' · match picked automatically'}
                            {execution.finished_at ? ` · took ${formatAiDuration(execution.created_at, execution.finished_at)}` : ''}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {!execution && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}
                    {execution && (
                        <Stack gap={2.5}>
                            <Stepper activeStep={activeStep} alternativeLabel>
                                {STEPS.map(stage => (
                                    <Step key={stage} completed={!!reached(stage) && stage !== failedAt && (execution.status === 'COMPLETED' || STEPS.indexOf(stage) < activeStep)}>
                                        <StepLabel error={stage === failedAt} optional={<Typography variant="caption">{time(reached(stage))}</Typography>}>
                                            {ARTICLE_STAGE_LABELS[stage]}
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            {execution.error && <Alert severity="error">Failed at {ARTICLE_STAGE_LABELS[execution.failed_stage ?? 'NOT_STARTED']}: {execution.error}</Alert>}
                            {execution.note && <Alert severity="info">{execution.note}</Alert>}
                            <Stack direction="row" gap={3} flexWrap="wrap" alignItems="flex-end">
                                <Meta label="Channel" value={execution.channel_id
                                    ? <Link component={RouterLink} to={`/marketing/betup-feed/channels/${execution.channel_id}`} onClick={onClose}>{execution.channel_name ?? `#${execution.channel_id}`}</Link>
                                    : '—'} />
                                <Meta label="Match" value={execution.match
                                    ? <Link component={RouterLink} to={`/matches/${execution.match.id}`} onClick={onClose}>{title} ({execution.match.league ?? execution.match.id})</Link>
                                    : '—'} />
                                <Meta label="Kick-off" value={execution.match?.start ? new Date(execution.match.start).toLocaleString() : '—'} />
                                <Meta label="AI cost" value={formatAiUsd(execution.cost_usd)} />
                                {execution.research_id && (
                                    <Meta label={execution.research_reused ? 'Research (reused)' : 'Research'}
                                        value={<Link component="button" variant="body2" onClick={() => setResearchOpen(true)}>
                                            #{execution.research_id}{execution.researched_at ? `, ${new Date(execution.researched_at).toLocaleString()}` : ''}
                                        </Link>} />
                                )}
                                <Meta label="Correlation id" value={<span style={{ fontFamily: 'monospace' }}>{execution.correlation_id ?? '—'}</span>} />
                                {execution.post_id && execution.channel_id && (onOpenPost
                                    ? <Button size="small" variant="outlined" onClick={() => onOpenPost(execution.post_id!, execution.channel_id!)}>Open post #{execution.post_id}</Button>
                                    : <Button size="small" variant="outlined" component={RouterLink} onClick={onClose}
                                        to={`/marketing/betup-feed/publishing?tab=all&channel=${execution.channel_id}`}>Post #{execution.post_id}</Button>)}
                                {execution.correlation_id && (
                                    <Button size="small" component={RouterLink} onClick={onClose}
                                        to={`/ai/workflows/${CHANNEL_ARTICLE_WORKFLOW}/runs/${encodeURIComponent(execution.correlation_id)}`}>
                                        All AI calls
                                    </Button>
                                )}
                            </Stack>
                            {execution.correlation_id && (
                                <>
                                    <Divider />
                                    <Typography variant="subtitle1">AI workflow run</Typography>
                                    {runLoading && <CircularProgress size={24} />}
                                    {!runLoading && !run && <Typography variant="body2" color="text.secondary">The AI hub has no calls for this correlation id yet.</Typography>}
                                    {run && <AiWorkflowRunView run={run} compact onNavigate={onClose} />}
                                </>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
            </Dialog>
            <MatchResearchDialog researchId={researchOpen && execution?.research_id ? execution.research_id : null} onClose={() => setResearchOpen(false)} />
        </>
    );
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography variant="body2" component="div">{value}</Typography>
        </Box>
    );
}
